/**
 * Board layout in screen pixels: a uniform camera that fits the shown words'
 * t-SNE positions into the measured board (with a minimum span, so a close
 * pair doesn't fill it), then a light relaxation that pushes apart nodes
 * closer than 2.5 radii. Shared by the live board and the report mini-graph.
 */

export interface Camera {
  /** Pixels per world unit. */
  s: number;
  tx: number;
  ty: number;
}

export interface Size {
  w: number;
  h: number;
}

/**
 * How the board lays out its words (the owner is comparing these):
 * - "current": fit every shown word, 3 light relaxation passes, 4 label slots.
 * - "declump": the same fit, then a stronger relaxation (8 iterations, 3
 *   radii, start/target pinned, a gentle spring back to t-SNE) and 8 label
 *   slots that prefer the side away from the path.
 * - "focus": fit the recent path plus the target (start only if it fits the
 *   zoom budget, else an edge indicator), with the light relaxation.
 * - "declump+focus": both.
 * - "declump+tight": experimental; as declump+focus, but the target too
 *   leaves the frame (edge indicator) when keeping it costs too much zoom.
 * - "options": declump, plus the current word's next-word options drawn as
 *   small candidate nodes; the fit covers current, options and target (start
 *   only within the zoom budget, else an edge indicator).
 */
export type BoardLayoutMode =
  | "current"
  | "declump"
  | "focus"
  | "declump+focus"
  | "declump+tight"
  | "options";
export const BOARD_LAYOUT_MODE: BoardLayoutMode = "declump+focus";

export const usesDeclump = (m: BoardLayoutMode) =>
  m.startsWith("declump") || m === "options";
export const usesFocus = (m: BoardLayoutMode) =>
  m === "focus" || m === "declump+focus" || m === "declump+tight";
export const usesTightFocus = (m: BoardLayoutMode) => m === "declump+tight";
export const usesOptions = (m: BoardLayoutMode) => m === "options";

/** Minimum span as a fraction of the whole graph's extent. */
export const MIN_SPAN_FRACTION = 0.35;
export const RELAX_PASSES = 3;
export const RELAX_RADII = 2.5;

/** 48px on a full board; smaller on a short report mini-graph. */
export function boardPadding(size: Size): number {
  return Math.max(28, Math.min(48, Math.min(size.w, size.h) * 0.15));
}

export function fitCamera(
  points: { x: number; y: number }[],
  size: Size,
  opts: { pad: number; minSpan: number },
): Camera {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const y0 = Math.min(...ys);
  const y1 = Math.max(...ys);
  const spanX = Math.max(x1 - x0, opts.minSpan, 1e-6);
  const spanY = Math.max(y1 - y0, opts.minSpan, 1e-6);
  const s = Math.max(
    1e-6,
    Math.min((size.w - 2 * opts.pad) / spanX, (size.h - 2 * opts.pad) / spanY),
  );
  return {
    s,
    tx: size.w / 2 - s * ((x0 + x1) / 2),
    ty: size.h / 2 - s * ((y0 + y1) / 2),
  };
}

export function project(cam: Camera, x: number, y: number) {
  return { px: cam.s * x + cam.tx, py: cam.s * y + cam.ty };
}

const extentCache = new WeakMap<object, number>();

/** Larger side of the whole graph's t-SNE bounding box (cached per graph). */
export function graphExtent(
  graph: Record<string, { tsne?: number[] | [number, number] }>,
): number {
  const hit = extentCache.get(graph);
  if (hit !== undefined) return hit;
  let x0 = Infinity;
  let x1 = -Infinity;
  let y0 = Infinity;
  let y1 = -Infinity;
  for (const key in graph) {
    const t = graph[key]?.tsne;
    if (!t) continue;
    x0 = Math.min(x0, t[0]);
    x1 = Math.max(x1, t[0]);
    y0 = Math.min(y0, t[1]);
    y1 = Math.max(y1, t[1]);
  }
  const extent = Number.isFinite(x0) ? Math.max(x1 - x0, y1 - y0) : 0;
  extentCache.set(graph, extent);
  return extent;
}

export interface RelaxNode {
  id: string;
  px: number;
  py: number;
  r: number;
}

/**
 * A few passes that push apart any pair closer than 2.5 radii (mean of the
 * two), symmetrically, keeping every node on the board. Deterministic.
 */
export function relaxPositions<T extends RelaxNode>(
  nodes: T[],
  size: Size,
  passes = RELAX_PASSES,
): T[] {
  const out = nodes.map((n) => ({ ...n }));
  for (let pass = 0; pass < passes; pass++) {
    let moved = false;
    for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i];
        const b = out[j];
        const min = (RELAX_RADII / 2) * (a.r + b.r);
        let dx = b.px - a.px;
        let dy = b.py - a.py;
        let d = Math.hypot(dx, dy);
        if (d >= min) continue;
        if (d < 1e-6) {
          const angle = (j * 2.399963) % (2 * Math.PI);
          dx = Math.cos(angle);
          dy = Math.sin(angle);
          d = 0;
        } else {
          dx /= d;
          dy /= d;
        }
        const push = (min - d) / 2;
        a.px -= dx * push;
        a.py -= dy * push;
        b.px += dx * push;
        b.py += dy * push;
        moved = true;
      }
    }
    for (const n of out) {
      n.px = Math.max(n.r, Math.min(size.w - n.r, n.px));
      n.py = Math.max(n.r, Math.min(size.h - n.r, n.py));
    }
    if (!moved) break;
  }
  return out;
}

export const DECLUMP_ITERATIONS = 8;
/** Minimum centre distance, in mean radii of the pair. */
export const DECLUMP_RADII = 3;
/** Share of the way back to its t-SNE spot each node moves per iteration. */
export const DECLUMP_SPRING = 0.15;
const CLEANUP_PASSES = 60;

export interface DeclumpOptions {
  /** Ids that never move (start and target). */
  pinned?: Set<string>;
  /**
   * Ids that hold still against ordinary nodes but yield to a pinned one
   * (the current word in "options" mode, so it never sits on the start).
   */
  soft?: Set<string>;
  /** Ids left out entirely (e.g. off-screen words under the focus fit). */
  skip?: Set<string>;
  iterations?: number;
  radii?: number;
  spring?: number;
}

/**
 * Stronger de-clumping: a few iterations of "spring back toward the t-SNE
 * position, then push apart any pair closer than `radii` mean radii", then
 * push-only passes until no pair is too close. Pinned nodes never move (the
 * other node of a pair takes the whole push). Deterministic.
 */
export function declumpPositions<T extends RelaxNode>(
  nodes: T[],
  size: Size,
  opts: DeclumpOptions = {},
): T[] {
  const pinned = opts.pinned ?? new Set<string>();
  const soft = opts.soft ?? new Set<string>();
  const skip = opts.skip ?? new Set<string>();
  const radii = opts.radii ?? DECLUMP_RADII;
  const spring = opts.spring ?? DECLUMP_SPRING;
  const iterations = opts.iterations ?? DECLUMP_ITERATIONS;
  const out = nodes.map((n) => ({ ...n }));
  const anchor = nodes.map((n) => ({ x: n.px, y: n.py }));
  const live = out.filter((n) => !skip.has(n.id));
  const fixed = (n: T) => pinned.has(n.id) || soft.has(n.id);
  /** 2 pinned, 1 soft, 0 free: the lower rank of a pair takes the push. */
  const rank = (n: T) => (pinned.has(n.id) ? 2 : soft.has(n.id) ? 1 : 0);

  const push = () => {
    let moved = false;
    for (let i = 0; i < live.length; i++) {
      for (let j = i + 1; j < live.length; j++) {
        const a = live[i];
        const b = live[j];
        const ra = rank(a);
        const rb = rank(b);
        if (ra === 2 && rb === 2) continue;
        const min = (radii / 2) * (a.r + b.r);
        let dx = b.px - a.px;
        let dy = b.py - a.py;
        let d = Math.hypot(dx, dy);
        if (d >= min - 1e-6) continue;
        if (d < 1e-6) {
          const angle = (j * 2.399963) % (2 * Math.PI);
          dx = Math.cos(angle);
          dy = Math.sin(angle);
          d = 0;
        } else {
          dx /= d;
          dy /= d;
        }
        const gap = min - d + 1e-3;
        const wa = ra === rb ? 0.5 : ra > rb ? 0 : 1;
        const wb = 1 - wa;
        a.px -= dx * gap * wa;
        a.py -= dy * gap * wa;
        b.px += dx * gap * wb;
        b.py += dy * gap * wb;
        moved = true;
      }
    }
    for (const n of live) {
      if (pinned.has(n.id)) continue;
      n.px = Math.max(n.r, Math.min(size.w - n.r, n.px));
      n.py = Math.max(n.r, Math.min(size.h - n.r, n.py));
    }
    return moved;
  };

  for (let it = 0; it < iterations; it++) {
    out.forEach((n, i) => {
      if (fixed(n) || skip.has(n.id)) return;
      n.px += (anchor[i].x - n.px) * spring;
      n.py += (anchor[i].y - n.py) * spring;
    });
    push();
  }
  for (let pass = 0; pass < CLEANUP_PASSES && push(); pass++);
  return out;
}
